import React, { useState, useEffect } from "react";
import { Form, Input, Button, Typography, Steps, Radio, Spin, Checkbox } from "antd";
import {
    UserOutlined,
    PhoneOutlined,
    ArrowLeftOutlined
} from "@ant-design/icons";
import { useNavigate, useLocation, Link } from "react-router-dom";
import AuthServ from "../service/AuthServ";
import "../css/loginAndSignUp.css";
import Notify from "../util/Notify";
import Navbar from "./Navbar";
import OTPInput from "../component/OTPInput";
import AuthVisualHero from "../component/AuthVisualHero";



const CustomerRegister = () => {
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const location = useLocation();

    // 0: Mobile & Verification, 1: Details
    const [currentStep, setCurrentStep] = useState(0);
    const [showOtpInput, setShowOtpInput] = useState(false);
    const [loading, setLoading] = useState(false);
    const [mobile, setMobile] = useState("");
    const [hasReferral, setHasReferral] = useState(false);
    const [agreementChecked, setAgreementChecked] = useState(false);

    // Sync mobile from state if redirected from Login page
    useEffect(() => {
        const phoneFromState = location.state?.phone;
        if (phoneFromState) {
            setMobile(phoneFromState);
            form.setFieldsValue({ mobile: phoneFromState });
        }
    }, [location.state, form]);

    // Handle Send OTP (Stay on Step 0, open OTP input inline)
    const handleSendOtp = async (values) => {
        setLoading(true);
        try {
            // Check if mobile already exists
            const checkRes = await AuthServ.checkMobileExists(values.mobile);
            if (checkRes?.data?.exists) {
                Notify.error("Account Already Exists", "This mobile number is already registered. Please login instead.");
                setLoading(false);
                return;
            }

            const response = await AuthServ.generateOtpByMobile(values.mobile, 'signup');
            setMobile(values.mobile);
            setShowOtpInput(true); // Open verification inline!
            Notify.success("OTP Sent", response?.message || `Verification code sent to +91 ${values.mobile}`);
        } catch (error) {
            Notify.error(error, "Failed to process request");
        } finally {
            setLoading(false);
        }
    };

    // Handle Verify OTP (Inline verification)
    const handleVerifyOtp = async (values) => {
        setLoading(true);
        try {
            const res = await AuthServ.verifyOtpByMobile(mobile, values.otp);

            if (!res.isNewUser) {
                Notify.warning("Account Exists", "This number is already registered. Redirecting to login...");
                setTimeout(() => navigate("/login"), 2000);
            } else {
                setCurrentStep(1); // Move to Personal Details page!
                Notify.success("Verified", "Mobile number verified successfully.");
            }
        } catch (error) {
            Notify.error(error, "Invalid OTP");
        } finally {
            setLoading(false);
        }
    };

    // Handle Final Registration (Step 1)
    const onFinish = async (values) => {
        setLoading(true);
        const finalData = {
            ...values,
            phone: mobile
        };

        try {
            await AuthServ.customerRegister(finalData);
            Notify.success("Registration Successful", "Welcome to Nearzon! Redirecting to login...");
            setTimeout(() => {
                navigate("/login");
            }, 2000);
        } catch (error) {
            Notify.error(error, "Could not complete registration");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="nz-auth-page-layout">
            <Navbar />
            <div className="nz-auth-card-wrapper">
                <div className="nz-auth-split-container">
                    {/* 🎨 Left Side - Visual Showcase Panel */}
                    <AuthVisualHero activeStep={2} />

                    {/* 🚪 Right Side - Elegant, ultra-clean form panel */}
                    <div className="nz-auth-form-side">
                        <div className="nz-split-form-wrapper" style={{ position: 'relative' }}>
                            {currentStep > 0 && (
                                <Button
                                    type="text"
                                    icon={<ArrowLeftOutlined style={{ fontSize: 18 }} />}
                                    onClick={() => {
                                        setCurrentStep(0);
                                    }}
                                    style={{ position: 'absolute', top: 8, left: 0, zIndex: 10 }}
                                />
                            )}

                            <h2>Create Account</h2>
                            <p className="nz-split-subtitle">
                                {currentStep === 0 && "Start your journey with Nearzon"}
                                {currentStep === 1 && "Finalize your profile details"}
                            </p>

                            <Steps
                                current={currentStep}
                                size="small"
                                className="custom-steps pcu-margin-bottom-24 pcu-padding-x-8"
                                items={[
                                    { title: 'Mobile & Verify', icon: <PhoneOutlined /> },
                                    { title: 'Details', icon: <UserOutlined /> },
                                ]}
                            />

                            <Spin spinning={loading}>
                                {/* STEP 0: MOBILE ENTRY & OPT VERIFICATION INLINE */}
                                {currentStep === 0 && (
                                    <Form
                                        form={form}
                                        layout="vertical"
                                        onFinish={showOtpInput ? handleVerifyOtp : handleSendOtp}
                                        requiredMark={false}
                                        size="large"
                                    >
                                        <Form.Item
                                            name="mobile"
                                            className="nz-premium-form-item"
                                            rules={[
                                                { required: true, message: "Please enter your mobile number" },
                                                { pattern: /^\d{10}$/, message: "Valid 10-digits required" }
                                            ]}
                                        >
                                            <Input
                                                prefix={
                                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                                        <span style={{ fontWeight: 700, color: '#475569', marginRight: '8px' }}>+91</span>
                                                        <div style={{ width: '1px', height: '18px', backgroundColor: '#cbd5e1', marginRight: '12px' }}></div>
                                                    </div>
                                                }
                                                placeholder="Mobile Number"
                                                maxLength={10}
                                                disabled={showOtpInput}
                                            />
                                        </Form.Item>

                                        {showOtpInput && (
                                            <>
                                                <div style={{ marginBottom: '12px', marginTop: '20px', textAlign: 'center' }}>
                                                    <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>
                                                        Enter 6-digit verification code
                                                    </span>
                                                </div>
                                                <Form.Item
                                                    name="otp"
                                                    rules={[
                                                        { required: true, message: "Please enter the OTP" },
                                                        { len: 6, message: "Must be 6 digits" }
                                                    ]}
                                                    style={{ marginBottom: '16px' }}
                                                >
                                                    <OTPInput length={6} />
                                                </Form.Item>
                                            </>
                                        )}

                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            block
                                            className="nz-split-submit-btn"
                                            style={{ marginTop: 20 }}
                                        >
                                            {showOtpInput ? "Verify & Continue" : "Continue"}
                                        </Button>

                                        {showOtpInput && (
                                            <div style={{ textAlign: 'center', marginTop: '16px' }}>
                                                <Button
                                                    type="link"
                                                    onClick={() => {
                                                        setShowOtpInput(false);
                                                        form.setFieldsValue({ otp: "" });
                                                    }}
                                                    style={{ color: '#64748b', fontSize: '13px', padding: 0 }}
                                                >
                                                    Wrong number? <span style={{ color: 'var(--nearzon-purple)', fontWeight: 'bold' }}>Change</span>
                                                </Button>
                                            </div>
                                        )}
                                    </Form>
                                )}

                                {/* STEP 1: PERSONAL DETAILS */}
                                {currentStep === 1 && (
                                    <Form layout="vertical" onFinish={onFinish} requiredMark={false} size="middle">
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
                                            <Form.Item
                                                name="firstName"
                                                rules={[{ required: true, message: "Required" }]}
                                                className="nz-premium-form-item"
                                            >
                                                <Input placeholder="First Name" />
                                            </Form.Item>
                                            <Form.Item
                                                name="lastName"
                                                rules={[{ required: true, message: "Required" }]}
                                                className="nz-premium-form-item"
                                            >
                                                <Input placeholder="Last Name" />
                                            </Form.Item>
                                        </div>

                                        <Form.Item
                                            name="email"
                                            className="nz-premium-form-item"
                                            rules={[
                                                { required: true, message: "Required" },
                                                { type: "email", message: "Invalid email" }
                                            ]}
                                        >
                                            <Input placeholder="Email Address" />
                                        </Form.Item>

                                        {!hasReferral && (
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 12px' }}>
                                                <Form.Item
                                                    name="password"
                                                    className="nz-premium-form-item"
                                                    rules={[
                                                        { required: true, message: "Required" },
                                                        { min: 6, message: "Minimum 6 characters" }
                                                    ]}
                                                >
                                                    <Input.Password placeholder="Password" />
                                                </Form.Item>

                                                <Form.Item
                                                    name="confirmPassword"
                                                    className="nz-premium-form-item"
                                                    dependencies={['password']}
                                                    rules={[
                                                        { required: true, message: "Required" },
                                                        ({ getFieldValue }) => ({
                                                            validator(_, value) {
                                                                if (!value || getFieldValue('password') === value) {
                                                                    return Promise.resolve();
                                                                }
                                                                return Promise.reject(new Error('Passwords do not match'));
                                                            },
                                                        }),
                                                    ]}
                                                >
                                                    <Input.Password placeholder="Confirm Password" />
                                                </Form.Item>
                                            </div>
                                        )}

                                        <Form.Item
                                            name="hasReferral"
                                            initialValue={false}
                                            className="nz-premium-form-item"
                                            style={{ marginBottom: '12px !important' }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '500' }}>
                                                    Have a referral code?
                                                </span>
                                                <Radio.Group onChange={(e) => setHasReferral(e.target.value)} size="small">
                                                    <Radio value={true} style={{ fontSize: '12px' }}>Yes</Radio>
                                                    <Radio value={false} style={{ fontSize: '12px' }}>No</Radio>
                                                </Radio.Group>
                                            </div>
                                        </Form.Item>

                                        {hasReferral && (
                                            <Form.Item
                                                name="referredBy"
                                                rules={[{ required: true, message: "Enter referral code" }]}
                                                className="nz-premium-form-item"
                                            >
                                                <Input placeholder="Enter Referral Code (Optional)" />
                                            </Form.Item>
                                        )}

                                        <Form.Item
                                            name="agreement"
                                            valuePropName="checked"
                                            rules={[
                                                {
                                                    validator: (_, value) =>
                                                        value ? Promise.resolve() : Promise.reject(new Error('Please agree to terms')),
                                                },
                                            ]}
                                            className="pcu-margin-bottom-12 pcu-margin-top-12"
                                        >
                                            <Checkbox style={{ fontSize: '11.5px', color: '#475569' }} onChange={(e) => setAgreementChecked(e.target.checked)}>
                                                I agree to the <Link to="/terms-and-conditions" target="_blank" style={{ color: 'var(--nearzon-purple)', fontSize: '11.5px', fontWeight: '600' }}>Terms & Conditions</Link> & <Link to="/privacy-policy" target="_blank" style={{ color: 'var(--nearzon-purple)', fontSize: '11.5px', fontWeight: '600' }}>Privacy Policy</Link>
                                            </Checkbox>
                                        </Form.Item>

                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            block
                                            className="nz-split-submit-btn"
                                            style={{ marginTop: '16px' }}
                                            disabled={!agreementChecked}
                                        >
                                            Complete Registration
                                        </Button>
                                    </Form>
                                )}
                            </Spin>

                            <div className="nz-split-footer">
                                Already have an account?{" "}
                                <Link to="/login" className="nz-split-footer-link">
                                    Login
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomerRegister;
